/* eslint-disable jsx-a11y/accessible-emoji */
/** @jsx jsx */

import type { ComponentType, ReactNode } from 'react';
import { useMemo } from 'react';
import { TextLinkButton } from '@balance-web/text-link';
import { jsx } from '@balance-web/core';
import { Flex } from '@balance-web/flex';
import type { IconProps } from '@balance-web/icon';
import { AlertOctagonIcon } from '@balance-web/icon/icons/AlertOctagonIcon';
import { AlertTriangleIcon } from '@balance-web/icon/icons/AlertTriangleIcon';
import { CheckCircleIcon } from '@balance-web/icon/icons/CheckCircleIcon';
import { ExternalLinkIcon } from '@balance-web/icon/icons/ExternalLinkIcon';
import { InfoIcon } from '@balance-web/icon/icons/InfoIcon';
import { Stack } from '@balance-web/stack';
import { Text } from '@balance-web/text';
import { useTheme } from '@balance-web/theme';

type Action = { action: () => void; label: string; externalLink?: boolean };

type Tones = 'informative' | 'cautious' | 'positive' | 'critical';

export type NoticeProps = {
  actions?: {
    primary?: Action;
    secondary?: Action;
  };
  children: ReactNode;
  size?: keyof typeof sizeToPadding;
  tone?: Tones;
  title?: string;
  /**
   * Replace the default icon.
   *
   * The default icons are appropriate for the "tone" and provide context for
   * users that aren't able to discern intent from colour alone. For continuity
   * within our products we recommend that this is rarely used.
   */
  icon?: ComponentType<IconProps>;
};

export const Notice = ({
  actions,
  children,
  size = 'medium',
  tone = 'informative',
  title,
  icon,
}: NoticeProps) => {
  const theme = useTheme();

  const Icon = icon || toneToIcon[tone];
  const padding = sizeToPadding[size];
  const iconWrapperHeightHack =
    size === 'small'
      ? `calc(${theme.typography.fontSize.small} * ${theme.typography.leading.base})`
      : undefined;

  const toneToBackgroundMap = useMemo(() => {
    return {
      informative: {
        background: theme.semantic.colour.reserved.info.container.background,
        color: theme.semantic.colour.reserved.info.container.foreground,
      },
      critical: {
        background: theme.semantic.colour.reserved.critical.container.background,
        color: theme.semantic.colour.reserved.critical.container.foreground,
      },
      positive: {
        background: theme.semantic.colour.reserved.success.container.background,
        color: theme.semantic.colour.reserved.success.container.foreground,
      },
      cautious: {
        background: theme.semantic.colour.reserved.warning.container.background,
        color: theme.semantic.colour.reserved.warning.container.foreground,
      },
    } as const;
  }, [theme.semantic]);

  return (
    <Flex
      border={tone}
      borderRadius={size}
      css={{
        background: toneToBackgroundMap[tone].background,
      }}
      gap={size}
      paddingX={padding.x}
      paddingY={padding.y}
      role="alert"
      aria-live="polite"
      // styles
      alignItems="flex-start"
    >
      <Flex alignItems="center" height={iconWrapperHeightHack}>
        <Icon
          css={{
            stroke: toneToBackgroundMap[tone].color,
          }}
          size={size}
        />
      </Flex>

      {/*
       * NOTE: `alignSelf="center"` is a little trick to center-align single
       * lines of text, while allowing text that wraps to be top-aligned,
       * relative to the icon.
       */}
      <Stack alignSelf="center" flex={1} gap="small">
        {title && <Text weight="bold">{title}</Text>}

        <Stack gap="medium">
          <Text size="small">{children}</Text>

          {actions && (
            <Flex gap="large">
              {actions.primary && (
                <Text size="small" weight="bold">
                  <TextLinkButton color="base" onClick={actions.primary.action}>
                    {actions.primary.label}
                    {actions.primary.externalLink ? (
                      <ExternalLinkIcon size="xsmall" />
                    ) : null}
                  </TextLinkButton>
                </Text>
              )}
              {actions.secondary && (
                <Text size="small" weight="bold">
                  <TextLinkButton
                    color="base"
                    onClick={actions.secondary.action}
                  >
                    {actions.secondary.label}
                    {actions.secondary.externalLink ? (
                      <ExternalLinkIcon size="xsmall" />
                    ) : null}
                  </TextLinkButton>
                </Text>
              )}
            </Flex>
          )}
        </Stack>
      </Stack>
    </Flex>
  );
};

// Maps
// ------------------------------

const toneToIcon = {
  informative: InfoIcon,
  critical: AlertOctagonIcon,
  positive: CheckCircleIcon,
  cautious: AlertTriangleIcon,
};

const sizeToPadding = {
  small: { x: 'medium', y: 'small' },
  medium: { x: 'large', y: 'large' },
} as const;
